import {
  Directive,
  EmbeddedViewRef,
  Input,
  TemplateRef,
  ViewContainerRef,
  ɵstringify as stringify,
} from '@angular/core';
import { AuthorizationService } from '../../authorization/authorization.service';
import { FeatureFlagService } from './feature-flag.service';

/**
 * @publicApi
 */
export class AppIfFeatureContext {
  public $implicit = false;
}

function assertTemplate(property: string, templateRef: TemplateRef<AppIfFeatureContext> | null): void {
  const isTemplateRefOrNull = !!(!templateRef || templateRef.createEmbeddedView);
  if (!isTemplateRefOrNull) {
    throw new Error(`${property} must be a TemplateRef, but received '${stringify(templateRef)}'.`);
  }
}

@Directive({
  selector: '[appIfFeature]',
})
export class IfFeatureDirective {
  private _context: AppIfFeatureContext = new AppIfFeatureContext();
  private _thenTemplateRef: TemplateRef<AppIfFeatureContext> | null = null;
  private _elseTemplateRef: TemplateRef<AppIfFeatureContext> | null = null;
  private _thenViewRef: EmbeddedViewRef<AppIfFeatureContext> | null = null;
  private _elseViewRef: EmbeddedViewRef<AppIfFeatureContext> | null = null;
  private _featureId: string | undefined;

  constructor(
    private _viewContainer: ViewContainerRef,
    templateRef: TemplateRef<AppIfFeatureContext>,
    private _featureFlagService: FeatureFlagService,
    private _authorizationService: AuthorizationService,
  ) {
    this._thenTemplateRef = templateRef;
    this._updateView();
  }

  @Input()
  set appIfFeature(value: string) {
    const featureIdExpression = value ? value : '';
    const not = featureIdExpression.startsWith('!');
    const featureId = not ? featureIdExpression.substr(1) : featureIdExpression;
    if (this._featureId !== featureId) {
      this._featureId = featureId;
      if (!featureId) {
        this._updateView();
        return;
      }
      this._featureFlagService.getFeatures().subscribe((features) => {
        const feature = features[featureId];
        // for two booleans, a != b is the equivalent of a logical XOR
        this._context.$implicit = (!!feature && feature.isEnabled) !== not;
        this._updateView();
      });
    }
  }

  @Input()
  set appIfFeatureThen(templateRef: TemplateRef<AppIfFeatureContext> | null) {
    assertTemplate('appIfFeatureThen', templateRef);
    this._thenTemplateRef = templateRef;
    this._thenViewRef = null; // clear previous view if any.
    this._updateView();
  }

  @Input()
  set appIfFeatureElse(templateRef: TemplateRef<AppIfFeatureContext> | null) {
    assertTemplate('appIfFeatureElse', templateRef);
    this._elseTemplateRef = templateRef;
    this._elseViewRef = null; // clear previous view if any.
    this._updateView();
  }

  /**
   * Assert the correct type of the expression bound to the `appIfFeature` input within the template.
   *
   * The presence of this method is a signal to the Ivy template type check compiler that when the
   * `AppIfFeature` structural directive renders its template, the type of the expression bound to `appIfFeature`
   * should be narrowed in some way. For `AppIfFeatureDirective`, it is narrowed to be non-null, which allows the
   * strictNullChecks feature of TypeScript to work with `AppIfFeatureDirective`.
   */
  static ngTemplateGuard_appIfFeature<E>(dir: IfFeatureDirective, expr: E): expr is NonNullable<E> {
    return true;
  }

  private _updateView(): void {
    // If the feature is not enabled, we allow access by default.
    if (this._context.$implicit) {
      if (!this._thenViewRef) {
        this._viewContainer.clear();
        this._elseViewRef = null;
        if (this._thenTemplateRef) {
          this._thenViewRef = this._viewContainer.createEmbeddedView(this._thenTemplateRef, this._context);
        }
      }
    } else {
      if (!this._elseViewRef) {
        this._viewContainer.clear();
        this._thenViewRef = null;
        if (this._elseTemplateRef) {
          this._elseViewRef = this._viewContainer.createEmbeddedView(this._elseTemplateRef, this._context);
        }
      }
    }
  }
}
